import type { ProjectSchemaJSON } from '@takeshape/schema';
import { getUserId } from 'app/auth/selectors';
import {
  ensureScratchBranchRoutine,
  type FetchSchemaRoutineMeta,
  fetchSchemaRoutine,
  fetchServiceLayersRoutine,
  type PostSchemaRoutineMeta,
  postSchemaRoutine
} from 'app/branches/actions';
import { getRoutineSaga } from 'app/common/utils/new-sagas';
import type { ProjectSchemaCompat } from 'app/compat/types';
import { getCurrentProjectId } from 'app/project/selectors';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import { getSchemaQuery, SCHEMA_FAILED_TO_LOAD } from '../api.js';
import { DEFAULT_BRANCH, SCRATCH_BRANCH } from '../constants.js';
import { getBranchByName, getDefaultRuntimeSchema } from '../selectors.js';
import type { Branch } from '../types.js';
import { branchToBranchQueryArgs } from '../utils.js';
import { fetchServiceLayersIfNotLoading } from './fetch-service-layers.js';

export const WORKING_COPY_ORIGIN_FETCH_SCHEMA = 'WORKING_COPY_ORIGIN_FETCH_SCHEMA';

export function* fetchSchemaSaga(
  _payload: undefined,
  { branch = DEFAULT_BRANCH, runtime = false, mode }: FetchSchemaRoutineMeta
): SagaIterator {
  const projectId = yield select(getCurrentProjectId);
  const userId = yield select(getUserId);
  const branchArgs = branchToBranchQueryArgs(projectId, branch, userId);

  mode = mode ?? (runtime ? 'runtime' : 'user');

  let userSchema: ProjectSchemaCompat | undefined;
  let runtimeSchema: ProjectSchemaCompat | undefined;

  if (mode === 'runtime') {
    runtimeSchema = yield call(getSchemaQuery, { schemaType: 'RUNTIME', ...branchArgs });
  }

  if (mode === 'user') {
    // For user schemas wait for service layers to load too
    [userSchema] = yield all([
      call(getSchemaQuery, { schemaType: 'USER', ...branchArgs }),
      call(fetchServiceLayersIfNotLoading, fetchServiceLayersRoutine.requested(undefined, { branch }))
    ]);
  }

  if (mode === 'all') {
    [userSchema, runtimeSchema] = yield all([
      call(getSchemaQuery, { schemaType: 'USER', ...branchArgs }),
      call(getSchemaQuery, { schemaType: 'RUNTIME', ...branchArgs }),
      call(fetchServiceLayersIfNotLoading, fetchServiceLayersRoutine.requested(undefined, { branch }))
    ]);
  }

  return { userSchema, runtimeSchema };
}

export const fetchSchemaRoutineSaga = getRoutineSaga(fetchSchemaRoutine, fetchSchemaSaga);

export function* fetchSchemaIfNotLoading(action: ActionType<typeof fetchSchemaRoutine.requested>): SagaIterator {
  const { runtime, branch = DEFAULT_BRANCH } = action.meta;
  const projectId = yield select(getCurrentProjectId);

  if (!projectId) {
    return null;
  }

  const branchState: Branch | undefined = yield select(getBranchByName, branch);

  if (branchState && (runtime ? branchState.runtimeSchema.loading : branchState.layers.user.loading)) {
    return;
  }

  return yield call(fetchSchemaRoutineSaga, action);
}

export function* fetchSchemaRejectedSaga({
  meta: { branch },
  payload
}: ActionType<typeof fetchSchemaRoutine.rejected>): SagaIterator {
  if (branch === SCRATCH_BRANCH) {
    if (payload === SCHEMA_FAILED_TO_LOAD) {
      yield put(ensureScratchBranchRoutine.requested(undefined, {}));
    }
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeEvery(fetchSchemaRoutine.requested, fetchSchemaIfNotLoading),
    takeEvery(fetchSchemaRoutine.rejected, fetchSchemaRejectedSaga)
  ]);
}

/**
 * Returns true if the fetch was rejected, false otherwise.
 */
export function* waitForFetchSchema(meta: FetchSchemaRoutineMeta): SagaIterator {
  while (true) {
    const action = yield take([fetchSchemaRoutine.fulfilled, fetchSchemaRoutine.rejected]);
    if (action.meta.runtime === meta.runtime && action.meta.branch === meta.branch) {
      return action.type === 'branches/FETCH_SCHEMA_REJECTED';
    }
  }
}

export function* waitForPostSchema(meta: PostSchemaRoutineMeta): SagaIterator {
  while (true) {
    const action = yield take([postSchemaRoutine.FULFILLED, postSchemaRoutine.REJECTED]);
    if (action.meta.runtime === meta.runtime && action.meta.branch === meta.branch) {
      return action.type === 'branches/POST_SCHEMA_FULFILLED';
    }
  }
}

export function* ensureDefaultRuntimeSchema(): SagaIterator<ProjectSchemaJSON> {
  let projectSchema: ProjectSchemaJSON | undefined = yield select(getDefaultRuntimeSchema);
  if (!projectSchema) {
    const meta: FetchSchemaRoutineMeta = { runtime: true };
    yield put(fetchSchemaRoutine.requested(undefined, meta));
    yield call(waitForFetchSchema, meta);

    projectSchema = yield select(getDefaultRuntimeSchema);
    if (!projectSchema) {
      throw new Error('Schema not found');
    }
  }

  return projectSchema;
}
